import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, Heart, ClipboardList } from 'lucide-react';
import { img, labelize, slugify, cx } from './ui';
import { usePublicData } from '../../../context/PublicDataContext';
import { useInquiryCart } from '../../../context/InquiryCartContext';
import { useWishlist } from '../../../context/WishlistContext';
import { useAuth } from '../../../context/AuthContext';
import MegaMenu from './MegaMenu';

const BUSINESS_LINKS = [
  { label: 'Dealers & Retailers', to: '/dealers' },
  { label: 'Hospitality & Projects', to: '/hospitality' },
  { label: 'Become a Partner', to: '/b2b' },
];

export default function PublicHeader({ onOpenSearch }) {
  const { categories, settings, siteName } = usePublicData();
  const { count, openCart } = useInquiryCart();
  const wishlist = useWishlist();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [panel, setPanel] = useState(null); // 'products' | 'business' | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAcc, setMobileAcc] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus whenever the route changes.
  useEffect(() => {
    setPanel(null);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const loginPath = user
    ? user.role === 'superadmin'
      ? '/superadmin'
      : user.role === 'admin'
      ? '/admin'
      : user.role === 'client'
      ? '/client'
      : '/worker'
    : '/login';
  // Website (moji.org.in / www.) links out to the app subdomain;
  // if we're already on app.moji.org.in, keep it a normal in-app link.
  // Only the real public website hosts link out; localhost / app. / previews stay in-app
  // (otherwise local testing jumps to the live app.moji.org.in).
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPublicSiteHost = hostname === 'moji.org.in' || hostname === 'www.moji.org.in';
  const loginTarget = isPublicSiteHost ? `https://app.moji.org.in${loginPath}` : loginPath;
  const loginLabel = user ? 'Dashboard' : 'Software Login';

  const goContact = () => {
    if (location.pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
    }
  };

  const NAV = [
    { label: 'Home', to: '/' },
    { label: 'Product Range', panel: 'products' },
    { label: 'Craftsmanship', to: '/craftsmanship' },
    { label: 'Projects', to: '/projects' },
    { label: 'Makers', to: '/makers' },
    { label: 'About', to: '/about' },
    { label: 'For Business', panel: 'business' },
  ];

  const isHome = location.pathname === '/';
  const solid = scrolled || panel || !isHome;
  // NOTE: background is applied via inline style (not a Tailwind arbitrary
  // opacity class like `bg-[#f4ece0f7]`) because that slash-opacity syntax
  // can silently fail to compile depending on the Tailwind setup, which left
  // the header background transparent while the text still switched to the
  // dark color — making the nav invisible over dark hero images.
  const headerTone = cx('fixed top-0 left-0 right-0 z-[60] transition-all duration-300', solid && 'backdrop-blur');
  const headerStyle = solid
    ? { backgroundColor: 'rgba(244,236,224,0.97)', boxShadow: '0 1px 0 rgba(36,24,17,0.08)' }
    : { backgroundColor: 'transparent' };
  const textTone = solid ? 'text-[#241811]' : 'text-[#f4ece0]';

  // Publish the real header height (shorter on phones, shrinks on scroll) so sticky
  // bars below it (category filters) sit right under it instead of at a fixed 104px.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const set = () => document.documentElement.style.setProperty('--public-header-h', `${el.offsetHeight}px`);
    set();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', set);
      return () => window.removeEventListener('resize', set);
    }
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className={headerTone}
      style={headerStyle}
      onMouseLeave={() => setPanel(null)}
    >
      {/* utility bar */}
      <div className={cx('border-b transition-colors', solid ? 'border-[#241811]/10' : 'border-white/15')}>
        <div className={cx('max-w-6xl mx-auto px-6 flex items-center justify-between transition-all', scrolled ? 'py-2.5' : 'py-3.5', textTone)}>
          <button onClick={onOpenSearch} className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] hover:opacity-70 transition-opacity" aria-label="Open search">
            <Search className="w-[18px] h-[18px]" />
            <span className="hidden sm:inline">Search</span>
          </button>

          <Link to="/" className="flex items-center gap-2.5" aria-label={siteName}>
            {settings.logo_url ? (
              <img src={img(settings.logo_url)} alt={siteName} className="h-8 w-auto object-contain" />
            ) : null}
            <span className="font-display font-semibold tracking-[0.05em] text-[15px] md:text-[17px]">
              {siteName.toUpperCase()}
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <Link to="/wishlist" className="relative hidden sm:block hover:opacity-70" aria-label="Wishlist">
              <Heart className="w-[18px] h-[18px]" />
              {wishlist.count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#b5622f] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {wishlist.count}
                </span>
              )}
            </Link>
            <button onClick={openCart} className="relative hover:opacity-70" aria-label="Inquiry cart">
              <ClipboardList className="w-[19px] h-[19px]" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#b5622f] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <a
              href={loginTarget}
              target="_blank"
              rel="noopener noreferrer"
              className={cx(
                'hidden md:inline-block text-[11px] uppercase tracking-[0.14em] border px-4 py-2 transition-all',
                solid ? 'border-[#241811]/25 hover:border-[#241811]' : 'border-white/40 hover:border-white'
              )}
            >
              {loginLabel}
            </a>
            <button className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
              <Menu />
            </button>
          </div>
        </div>
      </div>

      {/* primary nav (desktop) */}
      <nav className="hidden md:block">
        <div className={cx('max-w-6xl mx-auto px-6 flex items-center justify-center gap-9', textTone)}>
          {NAV.map((item) => {
            const isOpen = item.panel && panel === item.panel;
            const common = 'py-3.5 text-[12.5px] uppercase tracking-[0.14em] flex items-center gap-1.5 transition-colors hover:text-[#b5622f]';
            if (item.panel) {
              return (
                <button
                  key={item.label}
                  onMouseEnter={() => setPanel(item.panel)}
                  onClick={() => setPanel(isOpen ? null : item.panel)}
                  className={cx(common, isOpen && 'text-[#b5622f]')}
                  aria-expanded={isOpen}
                >
                  {item.label}
                  <ChevronDown className={cx('w-3.5 h-3.5 transition-transform', isOpen && 'rotate-180')} />
                </button>
              );
            }
            return (
              <Link key={item.label} to={item.to} onMouseEnter={() => setPanel(null)} className={common}>
                {item.label}
              </Link>
            );
          })}
          <button onClick={goContact} onMouseEnter={() => setPanel(null)} className={cx('py-3.5 text-[12.5px] uppercase tracking-[0.14em] hover:opacity-70 transition-opacity', solid ? 'text-[#b5622f]' : 'text-[#d98a52]')}>
            Contact
          </button>
        </div>
      </nav>

      {/* dropdown panels */}
      {panel && (
        <div className="hidden md:block absolute left-0 right-0 top-full bg-[#f4ece0] border-t border-[#241811]/10 shadow-xl">
          {panel === 'products' && <MegaMenu categories={categories} onNavigate={() => setPanel(null)} />}
          {panel === 'business' && (
            <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-3 gap-8">
              {BUSINESS_LINKS.map((b) => (
                <Link key={b.to} to={b.to} onClick={() => setPanel(null)} className="group border-b border-[#241811]/10 pb-4 hover:border-[#b5622f] transition-colors">
                  <div className="font-display text-xl group-hover:text-[#b5622f] transition-colors">{b.label}</div>
                  <div className="text-[13px] text-[#4a3d32] mt-1">Partner with our manufacturing floor.</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[65] bg-[#160f0a] text-[#f4ece0] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/12">
            <span className="font-display text-lg tracking-wide">{siteName.toUpperCase()}</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu"><X /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <MobileLink to="/" onClick={() => setMobileOpen(false)}>Home</MobileLink>

            <MobileAccordion
              label="Product Range"
              open={mobileAcc === 'products'}
              onToggle={() => setMobileAcc(mobileAcc === 'products' ? null : 'products')}
            >
              <Link to="/products" onClick={() => setMobileOpen(false)} className="block py-2 text-[#d98a52] text-sm uppercase tracking-[0.12em]">
                All products
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.name}
                  to={`/products?category=${encodeURIComponent(slugify(c.name))}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-2 text-[15px]"
                >
                  {labelize(c.name)} <span className="text-xs opacity-60">{c.count}</span>
                </Link>
              ))}
            </MobileAccordion>

            <MobileLink to="/craftsmanship" onClick={() => setMobileOpen(false)}>Craftsmanship</MobileLink>
            <MobileLink to="/projects" onClick={() => setMobileOpen(false)}>Projects</MobileLink>
            <MobileLink to="/makers" onClick={() => setMobileOpen(false)}>Makers</MobileLink>
            <MobileLink to="/about" onClick={() => setMobileOpen(false)}>About</MobileLink>

            <MobileAccordion
              label="For Business"
              open={mobileAcc === 'business'}
              onToggle={() => setMobileAcc(mobileAcc === 'business' ? null : 'business')}
            >
              {BUSINESS_LINKS.map((b) => (
                <Link key={b.to} to={b.to} onClick={() => setMobileOpen(false)} className="block py-2 text-[15px]">
                  {b.label}
                </Link>
              ))}
            </MobileAccordion>

            <button
              onClick={() => { setMobileOpen(false); goContact(); }}
              className="block w-full text-left py-3 border-b border-white/12 text-[16px] uppercase tracking-[0.12em] text-[#d98a52]"
            >
              Contact
            </button>
          </div>
          <div className="px-6 py-5 border-t border-white/12">
            <a href={loginTarget} target="_blank" rel="noopener noreferrer" className="block text-center border border-white/40 py-3 text-[12px] uppercase tracking-[0.16em]">
              {loginLabel}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, onClick, children }) {
  return (
    <Link to={to} onClick={onClick} className="block py-3 border-b border-white/12 text-[16px] uppercase tracking-[0.12em]">
      {children}
    </Link>
  );
}

function MobileAccordion({ label, open, onToggle, children }) {
  return (
    <div className="border-b border-white/12">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-3 text-[16px] uppercase tracking-[0.12em]">
        {label}
        <ChevronDown className={cx('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="pb-3 pl-1">{children}</div>}
    </div>
  );
}
